<template>
  <v-row
    id="search-sort"
    class="i-search-sort"
    :dense="$vuetifyDisplay.mdAndDown"
    v-if="inRegisteredPage"
  >
    <v-dialog
      v-model="dateRangeDialog"
      width="300px"
      persistent
      @click:outside="resetDateRangeDialog"
      @keydown.esc="resetDateRangeDialog"
    >
      <v-date-picker
        v-model="dateRangeDialogSelection"
        :max="datePickerMaxDate"
        :min="datePickerMinDate"
        range
      >
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="resetDateRangeDialog()">
          Cancel
        </v-btn>
        <v-btn
          text
          color="primary"
          :disabled="dateRangeDialogSelection.length !== 2"
          @click="confirmDateRangeDialog()"
        >
          Apply
        </v-btn>
      </v-date-picker>
    </v-dialog>

    <v-col
      :cols="colSizes.search.base"
      :sm="colSizes.search.sm"
      :md="colSizes.search.md"
      :lg="colSizes.search.lg"
    >
      <v-card class="card">
        <div class="search-div">
          <v-btn
            class="search-btn"
            elevation="0"
            :loading="loading"
            @click="searchImmediately(searchText)"
          >
            Search <v-icon class="ml-2 pt-1" size="20px">mdi-magnify</v-icon>
          </v-btn>
          <v-text-field
            solo
            flat
            hide-details
            clearable
            autocomplete="off"
            ref="input"
            v-model="searchText"
            :disabled="loading"
            :loading="loading"
            :placeholder="
              sortByPlaceholder.replace(
                'Retailer',
                isILR ? 'Customer' : 'Retailer'
              )
            "
            @input="handleInputChange(searchText)"
            @click:clear="searchByText()"
            @keyup.enter="searchImmediately(searchText)"
          >
          </v-text-field>
        </div>
      </v-card>
    </v-col>

    <v-col
      :cols="colSizes.calendar.base"
      :sm="colSizes.calendar.sm"
      :md="colSizes.calendar.md"
      :lg="colSizes.calendar.lg"
    >
      <v-card class="card">
        <div class="list-div">
          <div
            class="list-div-icon"
            v-tooltip="{
              content: `Date Range${dateRangeColumnName ? ` by ${dateRangeColumnName}` : ''}`
            }"
          >
            <v-icon class="list-icon">mdi-calendar-range</v-icon>
          </div>
          <v-select
            attach
            class="date-picker"
            solo
            flat
            hide-details
            placeholder="Sort by"
            :value="dateRange"
            :items="dateRangeOptions"
          >
            <template v-slot:selection="{ item }">
              <span class="text-no-wrap">{{ item.text }}</span>
              <template
                v-if="item.value !== 'default' && dateRange === item.value"
              >
                <v-spacer></v-spacer>
                <div class="date-range ml-1 mr-0 pa-1 text-no-wrap">
                  <span
                    v-if="searchByCreatedAtStart"
                    class="date-range-selection-text"
                    >{{
                      `${displaySearchByCreatedAtStart} ~ ${displaySearchByCreatedAtEnd}`
                    }}</span
                  >
                </div>
              </template>
            </template>
            <template v-slot:item="{ item, attrs, on }">
              <v-list-item
                v-bind="attrs"
                v-on="on"
                @click="handleDateRange(item.value)"
              >
                {{ item.text }}
              </v-list-item>
            </template>
          </v-select>
        </div>
      </v-card>
    </v-col>

    <v-col
      v-if="sortByOptions.length > 0"
      :cols="colSizes.sort.base"
      :sm="colSizes.sort.sm"
      :md="colSizes.sort.md"
      :lg="colSizes.sort.lg"
    >
      <v-card class="card">
        <div class="list-div">
          <div class="list-div-icon" v-tooltip="'Sort By'">
            <v-icon class="list-icon">mdi-sort-ascending</v-icon>
          </div>
          <v-select
            attach
            class="filter"
            flat
            hide-details
            :items="sortByOptions"
            placeholder="Sort by"
            solo
            :value="sortBy"
          >
            <template v-slot:selection="{ item }">
              <span>{{ item.text }}</span>
              <template v-if="item.value !== 'default'">
                <v-icon v-if="item.asc" class="sort-icon ml-3"
                  >mdi-arrow-up</v-icon
                >
                <v-icon v-else class="sort-icon ml-3">mdi-arrow-down</v-icon>
              </template>
            </template>

            <template v-slot:item="{ item, attrs, on }">
              <v-list-item
                v-bind="attrs"
                v-on="on"
                class="flex"
                @click="sortElements(item.value)"
              >
                <span>{{ item.text }}</span>
                <template v-if="item.value !== 'default'">
                  <template v-if="sortBy === item.value">
                    <v-icon
                      v-if="item.asc"
                      class="float-right sort-icon mt-1 ml-2"
                      >mdi-arrow-down</v-icon
                    >
                    <v-icon v-else class="float-right sort-icon mt-1 ml-2"
                      >mdi-arrow-up</v-icon
                    >
                  </template>
                  <template v-else>
                    <v-icon
                      v-if="item.asc"
                      class="float-right sort-icon mt-1 ml-2"
                      >mdi-arrow-up</v-icon
                    >
                    <v-icon v-else class="float-right sort-icon mt-1 ml-2"
                      >mdi-arrow-down</v-icon
                    >
                  </template>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </div>
      </v-card>
    </v-col>

    <v-spacer></v-spacer>

    <v-col
      v-if="showExportButton"
      :cols="colSizes.export.base"
      :sm="colSizes.export.sm"
      :md="colSizes.export.md"
      :lg="colSizes.export.lg"
      class="text-center"
    >
      <v-btn
        class="g-full-width i-btn-block-primary c-large"
        :disabled="disableExportButton || !resultsQuantity"
        :loading="fetchingExportFile || loading"
        v-tooltip="{
          content: `Max. export ${maxExportableItems} results`,
          visible:
            maxExportableItems === -1 || $vuetifyDisplay.mdAndDown
              ? false
              : undefined
        }"
        @click="handleExportToFile"
      >
        <span>{{ exportButtonText }}</span>
        <v-icon color="white" class="ml-1">{{ exportButtonIcon }}</v-icon>
      </v-btn>
    </v-col>
  </v-row>
</template>

<script>
import moment from 'moment-timezone'
import { mapGetters } from 'vuex'
const DEFAULT_COL_SIZES_NO_EXPORT = {
  search: {
    base: 12,
    sm: 12,
    md: 6,
    lg: 6
  },
  calendar: {
    base: 12,
    sm: 6,
    md: 3,
    lg: 3
  },
  sort: {
    base: 12,
    sm: 6,
    md: 3,
    lg: 3
  }
}

const DEFAULT_COL_SIZES_WITH_EXPORT = {
  search: {
    base: 12,
    sm: 12,
    md: 12,
    lg: 4
  },
  calendar: {
    base: 12,
    sm: 6,
    md: 3,
    lg: 3
  },
  sort: {
    base: 12,
    sm: 6,
    md: 3,
    lg: 3
  },
  export: {
    base: 12,
    sm: 12,
    md: 3,
    lg: 2
  }
}

const DEBOUNCE_SEARCH_TIMER = 2000

const DATE_RANGE_PAST_OPTIONS = [
  { text: 'Default (All)', value: 'default' },
  { text: 'Last 7 days', value: 'lastSevenDays' },
  { text: 'Last 30 days', value: 'lastThirtyDays' },
  { text: 'Last 90 days', value: 'lastNinetyDays' },
  { text: 'Custom', value: 'custom' }
]

const DATE_RANGE_FUTURE_OPTIONS = [
  { text: 'Default (All)', value: 'default' },
  { text: 'Next 7 days', value: 'nextSevenDays' },
  { text: 'Next 30 days', value: 'nextThirtyDays' },
  { text: 'Next 90 days', value: 'nextNinetyDays' },
  { text: 'Custom', value: 'custom' }
]

export default {
  props: {
    initialDateRange: {
      default: 'default',
      required: false,
      type: String
    },
    executeOnMount: {
      default: false,
      required: false,
      type: Boolean
    },
    exportButtonText: {
      default: 'Export Results',
      required: false,
      type: String
    },
    exportButtonIcon: {
      default: 'mdi-file-excel-outline',
      required: false,
      type: String
    },
    dateRangeColumnName: {
      default: '',
      required: false,
      type: String
    },
    dateRangeStartParameterName: {
      default: 'searchByCreatedAtStart',
      required: false,
      type: String
    },
    dateRangeEndParameterName: {
      default: 'searchByCreatedAtEnd',
      required: false,
      type: String
    },
    disableExportButton: {
      default: false,
      required: false,
      type: Boolean
    },
    maxExportableItems: {
      default: 50,
      required: false,
      type: Number
    },
    fetchingExportFile: {
      default: false,
      required: false,
      type: Boolean
    },
    loading: {
      default: false,
      required: false,
      type: Boolean
    },
    resultsQuantity: {
      default: 0,
      required: false,
      type: Number
    },
    routeName: {
      required: true,
      type: String
    },
    showExportButton: {
      default: false,
      required: false,
      type: Boolean
    },
    sortOptions: {
      required: false,
      type: Array
    }
  },

  mounted() {
    if (this.executeOnMount) {
      this.handleDateRange(this.initialDateRange)
    }
  },

  data: () => ({
    currentDate: moment().format(),
    previousQuery: '',
    dateRange: 'default',
    dateRangeBefore: 'default',
    dateRangeDialog: false,
    dateRangeDialogSelection: [],
    debounceSearch: null,
    searchedImmediately: false,
    searchText: '',
    searchByCreatedAtEnd: '',
    searchByCreatedAtStart: '',
    sortBy: 'default',
    sortQuery: null
  }),

  computed: {
    ...mapGetters('tenant', ['isILR']),
    REGISTERED_PAGES() {
      return [
        {
          name: 'purchase-orders-history',
          useDateRangeInFuture: false,
          sortByOptions: [
            { text: 'Default', value: 'default', asc: false },
            { text: 'PO Number', value: 'supplierOrderNumber', asc: false },
            { text: 'Order Date & Time', value: 'createdAt', asc: false },
            {
              text: `${this.isILR ? 'Customer' : 'Retailer'}`,
              value: 'store.name',
              asc: false
            },
            { text: 'Payment Type', value: 'paymentType', asc: false },
            { text: 'Postcode', value: 'deliveryAddress.postCode', asc: false },
            { text: 'State', value: 'deliveryAddress.state', asc: false }
          ],
          sortByPlaceholder: 'Search by PO Number and Retailer'
        },
        {
          name: 'order-queries-history',
          useDateRangeInFuture: false,
          sortByOptions: [
            { text: 'Default', value: 'default', asc: false },
            {
              text: 'Order Query Reference Number',
              value: 'referenceNumber',
              asc: false
            },
            { text: 'PO Number', value: 'supplierOrderNumber', asc: false },
            { text: 'Order Date & Time', value: 'orderDateTime', asc: false },
            { text: 'Created Date', value: 'createdAt', asc: false },
            { text: 'Resolved Date', value: 'resolvedAt', asc: false },
            { text: 'Payment Type', value: 'paymentType', asc: false },
            {
              text: `${this.isILR ? 'Customer' : 'Retailer'}`,
              value: 'storeName',
              asc: false
            }
          ],
          sortByPlaceholder: `Search by Order Query Reference Number, Retailer Order Number, PO Number, ${this.isILR ? 'Customer' : 'Retailer'}`
        },
        {
          name: 'credit-returns-history',
          useDateRangeInFuture: false,
          sortByOptions: [
            { text: 'Default', value: 'default', asc: false },
            {
              text: 'Credit Return Reference Number',
              value: 'referenceNumber',
              asc: false
            },
            { text: 'Created Date', value: 'createdAt', asc: false },
            { text: 'Resolved Date', value: 'resolvedAt', asc: false },
            {
              text: `${this.isILR ? 'Customer' : 'Retailer'}`,
              value: 'store.name',
              asc: false
            }
          ],
          sortByPlaceholder:
            'Search by Credit Return Reference Number and Retailer'
        },
        {
          name: 'payment-adjustments',
          useDateRangeInFuture: false,
          sortByOptions: [
            { text: 'Default', value: 'default', asc: false },
            {
              text: 'Payment Adjustment Reference Number',
              value: 'referenceNumber',
              asc: false
            },
            {
              text: `${this.isILR ? 'Customer' : 'Retailer'}`,
              value: 'store.name',
              asc: false
            }
          ],
          sortByPlaceholder:
            'Search by Payment Adjustment Reference Number, Related To and Retailer'
        },
        {
          name: 'transactions',
          useDateRangeInFuture: false,
          sortByOptions: [], // no server side sorting
          sortByPlaceholder: 'Search by Reference, Order Number, Retailer'
        },
        {
          name: 'fees',
          useDateRangeInFuture: false,
          sortByOptions: [], // no server side sorting
          sortByPlaceholder: 'Search by Invoice Number, Reference, Retailer'
        }
      ]
    },
    datePickerMaxDate() {
      return this.currentPageData.useDateRangeInFuture
        ? moment().add(365, 'days').format()
        : this.currentDate
    },

    datePickerMinDate() {
      return this.currentPageData.useDateRangeInFuture
        ? this.currentDate
        : undefined
    },

    dateRangeOptions() {
      return this.currentPageData.useDateRangeInFuture
        ? DATE_RANGE_FUTURE_OPTIONS
        : DATE_RANGE_PAST_OPTIONS
    },

    displaySearchByCreatedAtStart() {
      return this.formatDateForDisplay(this.searchByCreatedAtStart)
    },

    displaySearchByCreatedAtEnd() {
      return this.formatDateForDisplay(this.searchByCreatedAtEnd)
    },

    inRegisteredPage() {
      return this.REGISTERED_PAGES.map((page) => page.name).includes(
        this.routeName
      )
    },

    currentPageData() {
      return this.REGISTERED_PAGES.find((page) => page.name === this.routeName)
    },

    colSizes() {
      return this.showExportButton
        ? DEFAULT_COL_SIZES_WITH_EXPORT
        : DEFAULT_COL_SIZES_NO_EXPORT
    },

    sortByOptions() {
      if (!this.inRegisteredPage) return []
      return this.sortOptions || this.currentPageData?.sortByOptions
    },

    sortByPlaceholder() {
      return this.currentPageData?.sortByPlaceholder
    }
  },

  methods: {
    // ***
    // Functions related to exporting
    // ***

    handleExportToFile() {
      if (this.searchText === null) this.searchText = ''
      this.$emit('fetchExportFile', this.buildQuery())
    },

    // ***
    // Functions related to search query
    // ***

    emitSearchQuery() {
      if (this.searchText === null) this.searchText = ''

      const query = this.buildQuery()

      const payload = {
        query,
        restartPagination: true,
        searchText: this.searchText,
        sortQuery: this.sortQuery,
        startDate: this.searchByCreatedAtStart,
        endDate: this.searchByCreatedAtEnd
      }

      if (query !== this.previousQuery) {
        this.previousQuery = query
      } else {
        payload.restartPagination = false
      }

      this.$emit('searchQuery', payload)
    },

    buildQuery() {
      let builtQuery = `search=${this.searchText}&${this.dateRangeEndParameterName}=${this.searchByCreatedAtEnd}&${this.dateRangeStartParameterName}=${this.searchByCreatedAtStart}`

      if (this.sortQuery) builtQuery += `&sort=${this.sortQuery}`

      return builtQuery
    },

    // ***
    // Functions related to text search
    // ***

    handleInputChange(searchText) {
      // Only if text exists otherwise reset triggers it again
      if (searchText) {
        clearTimeout(this.debounceSearch)
        this.debounceSearch = setTimeout(() => {
          if (!this.searchedImmediately) this.searchByText(searchText)
          else this.searchedImmediately = false
        }, DEBOUNCE_SEARCH_TIMER)
      }
    },

    searchByText(searchText) {
      this.searchText = searchText || ''
      this.emitSearchQuery()
    },

    searchImmediately(searchText) {
      this.searchedImmediately = true
      this.searchByText(searchText)
    },

    // ***
    // Functions related to sorting
    // ***

    sortElements(sortBy) {
      if (sortBy === this.sortBy) {
        // If is same sort option, switch between ascending and descending order
        for (const option of this.sortByOptions) {
          if (option.value === sortBy) {
            option.asc = !option.asc
            this.sortQuery = option.asc
              ? `${option.value} asc`
              : `${option.value} desc`
          }
        }
      } else {
        // If not, reset asc order of all options
        for (const option of this.sortByOptions) {
          option.asc = false
          if (option.value === sortBy) {
            this.sortQuery = option.asc
              ? `${option.value} asc`
              : `${option.value} desc`
          }
        }
      }

      // Default selection does not have sort but rather removes it
      if (sortBy === 'default') this.sortQuery = null

      this.sortBy = sortBy
      this.emitSearchQuery()
    },

    // Function called by external components
    // Change function name carefully in all places
    sortReset() {
      this.sortBy = 'default'
    },

    // ***
    // Functions related to date scoping
    // ***

    handleDateRange(range) {
      const CREATED_AT_END = 0
      if (range === 'custom') this.openDateRangeDialog()
      else if (range === 'lastSevenDays') this.setDateRange(CREATED_AT_END, -7)
      else if (range === 'lastThirtyDays')
        this.setDateRange(CREATED_AT_END, -30)
      else if (range === 'lastNinetyDays')
        this.setDateRange(CREATED_AT_END, -90)
      else if (range === 'nextSevenDays') this.setDateRange(7, 1)
      else if (range === 'nextThirtyDays') this.setDateRange(30, 1)
      else if (range === 'nextNinetyDays') this.setDateRange(90, 1)
      else this.setDateRange()

      // For Custom Date selection, if user cancels, we need to remember previous selection
      if (range === 'custom') this.dateRangeBefore = this.dateRange

      this.dateRange = range
    },

    setDateRange(createdAtEndInDays, createdAtStartInDays) {
      if (
        createdAtEndInDays === undefined ||
        createdAtStartInDays === undefined
      ) {
        this.searchByCreatedAtEnd = this.searchByCreatedAtStart = ''
      } else {
        this.searchByCreatedAtEnd = this.formatDateForApi(
          moment().add(createdAtEndInDays, 'days')
        )
        this.searchByCreatedAtStart = this.formatDateForApi(
          moment().add(createdAtStartInDays, 'days')
        )
      }

      this.emitSearchQuery()
    },

    formatDateForApi(date) {
      return moment(date).format('YYYY-MM-DD')
    },

    formatDateForDisplay(date) {
      return moment(date).format('DD/MM/YYYY')
    },

    openDateRangeDialog() {
      this.dateRangeDialogSelection[1] = this.searchByCreatedAtEnd
      this.dateRangeDialogSelection[0] = this.searchByCreatedAtStart
      this.dateRangeDialog = true
    },

    confirmDateRangeDialog() {
      // Dont need to use setDateRange func as dates already come in correct format
      ;[this.searchByCreatedAtEnd, this.searchByCreatedAtStart] =
        this.dateRangeDialogSelection[1] > this.dateRangeDialogSelection[0]
          ? [this.dateRangeDialogSelection[1], this.dateRangeDialogSelection[0]]
          : [this.dateRangeDialogSelection[0], this.dateRangeDialogSelection[1]]

      this.dateRangeBefore = this.dateRange = 'custom'
      this.dateRangeDialog = false
      this.emitSearchQuery()
    },

    resetDateRangeDialog() {
      // Date Range selection restored to previous one if canceled
      this.dateRange = this.dateRangeBefore
      this.dateRangeDialog = false
    }
  }
}
</script>

<style lang="scss">
.date-picker {
  .v-input__control input {
    max-width: 0px !important;
  }

  .v-list-item {
    display: flex;
    padding: 0;
    div {
      padding: 10px 16px;
      width: 100%;
    }
  }
}

.date-range-selection-text {
  font-size: 0.7rem !important;
}

.p-date-range-text {
  background-color: #e5e8f0;
  border-radius: 4px;
  padding: 3px 10px 4px 10px;
  span {
    font-size: 14px !important;
    color: $primary-color;
  }
}
</style>

<style lang="scss" scoped>
.sort-icon {
  &:before {
    font-size: 20px;
  }
}
</style>
